import { articlesFromFilenameList } from "@/src/utils/articleSyncHelpers";
import { type DealerLogoData } from "@/src/utils/dealerLogoUtils";

import {
  type ArticleRouteData,
  loadArticleRouteData
} from "./articleRouteData";
import { loadDealerLogoRouteData } from "./dealerLogoRouteData";

// How many articles the home page shows; mirrors HomePageArticleBlock.
export const HOME_ARTICLE_COUNT = 4;

export interface HomeRouteData {
  dealerLogos: DealerLogoData;
  articles: ArticleRouteData;
}

const EMPTY_ARTICLES: ArticleRouteData = {
  filenames: [],
  filenameToSlug: {},
  articles: {}
};

// The home page only shows the newest few articles, so the inlined route data
// carries just those instead of the whole archive.
const pickNewestArticles = (data: ArticleRouteData): ArticleRouteData => {
  const newest = articlesFromFilenameList(
    data.filenames,
    data.filenameToSlug,
    data.articles
  ).slice(0, HOME_ARTICLE_COUNT);
  const newestSlugs = new Set(
    Object.keys(data.articles).filter((slug) =>
      newest.includes(data.articles[slug])
    )
  );
  const filenames = data.filenames.filter((fn) =>
    newestSlugs.has(data.filenameToSlug[fn])
  );

  return {
    filenames,
    filenameToSlug: Object.fromEntries(
      filenames.map((fn) => [fn, data.filenameToSlug[fn]])
    ),
    articles: Object.fromEntries(
      [...newestSlugs].map((slug) => [slug, data.articles[slug]])
    )
  };
};

// Build-time loader for the prerendered home pages. Both fetches degrade to
// empty data on failure so a flaky bucket never fails the build; build:verify
// is what catches a prerender that shipped without logos.
export const loadHomeRouteData = async (): Promise<HomeRouteData> => {
  const [dealerLogos, articles] = await Promise.all([
    loadDealerLogoRouteData(),
    loadArticleRouteData().then(pickNewestArticles, () => EMPTY_ARTICLES)
  ]);
  return { dealerLogos, articles };
};

// Browser fallback when there is no prerendered data (e.g. a client-side
// navigation from a page that was never prerendered). Articles are left empty
// here: `useGetArticles` on the home page already syncs them incrementally, and
// downloading the whole archive just to show four cards would be wasteful.
export const loadHomeRouteDataOnClient = async (): Promise<HomeRouteData> => ({
  dealerLogos: await loadDealerLogoRouteData(),
  articles: EMPTY_ARTICLES
});
